import { CartItem, Item, QuoteResponse } from "../types/item";
import {
  lineIdentity,
  toQuoteItem,
  cloneItem,
  missingRequiredGroup,
} from "../utils/menu";
import { persist } from "zustand/middleware";
import { create } from "zustand";
import { Tools } from "../utils";
import useAppStore from "./app";
import APIs from "../apis";

interface CheckoutPrefs {
  email?: string;
  deliveryInfo?: {
    fullName?: string;
    phone?: string;
    altPhone?: string;
    deliveryAddress?: string;
    nearestLandmark?: string;
    notes?: string;
  };
  pickupSlotMinutes?: number;
  paymentMethod?: string;
  isGift?: boolean;
  giftMessage?: string;
  changeRequest?: string;
}

interface CartState {
  items: CartItem[];
  tax: number;
  promoCode?: string;
  quoteId?: string;
  pricesIncludeTax?: boolean;
  discount?: number;
  discountBreakdown?: QuoteResponse["discountBreakdown"];
  subtotal: number;
  taxPercent: number;
  grandtotal: number;
  deliveryCharges: number;
  totalItemsInCart: number;
  calculatingQuote: boolean;
  checkoutPrefs: CheckoutPrefs;
  setPromoCode: (promoCode?: string) => void;
  setCheckoutPrefs: (prefs: CheckoutPrefs) => void;
  getItemIndex: (item: Item) => number;
  refreshQuote: () => Promise<void>;
  checkout: (data?: any) => Promise<any>;
  setCart: (
    items: Item[],
    options?: { dropUnavailable?: boolean },
  ) => Promise<void>;
  add: (item: Item, extra?: any) => Promise<void>;
  remove: (item: CartItem, forever?: boolean) => Promise<void>;
  reset: () => void;
}

const emptyTotals = {
  tax: 0,
  subtotal: 0,
  taxPercent: 0,
  grandtotal: 0,
  deliveryCharges: 0,
  discount: 0,
  quoteId: undefined as string | undefined,
  pricesIncludeTax: true,
  discountBreakdown: undefined as QuoteResponse["discountBreakdown"],
};

const place = () => useAppStore.getState().location;

const quoteSignature = (
  location: ReturnType<typeof place>,
  promoCode: string | undefined,
  items: CartItem[],
) =>
  JSON.stringify({
    area: location?.area || "",
    branch: location?.branch || "",
    orderType: location?.orderType || "",
    promo: promoCode || "",
    lines: items.map((item) => ({
      k: lineIdentity(item),
      q: item.quantity,
    })),
  });

let lastQuoteSignature = "";
let inFlight: Promise<void> | null = null;
let inFlightSig = "";
let quoteTimer: ReturnType<typeof setTimeout> | undefined;
let quoteWaiters: Array<{
  resolve: () => void;
  reject: (error: unknown) => void;
}> = [];

const scheduleQuote = (run: () => Promise<void>) =>
  new Promise<void>((resolve, reject) => {
    quoteWaiters.push({ resolve, reject });
    if (quoteTimer) clearTimeout(quoteTimer);
    quoteTimer = setTimeout(async () => {
      const waiters = quoteWaiters;
      quoteWaiters = [];
      quoteTimer = undefined;
      try {
        await run();
        waiters.forEach((waiter) => waiter.resolve());
      } catch (error) {
        waiters.forEach((waiter) => waiter.reject(error));
      }
    }, 50);
  });

const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      ...emptyTotals,
      calculatingQuote: false,
      checkoutPrefs: {},
      totalItemsInCart: 0,
      setPromoCode: (promoCode) => {
        if ((get().promoCode || "") === (promoCode || "")) return;
        set({ promoCode });
        get().refreshQuote();
      },
      setCheckoutPrefs: (prefs) => {
        set({ checkoutPrefs: { ...get().checkoutPrefs, ...prefs } });
      },
      getItemIndex: (item: Item) =>
        get().items.findIndex((_) => lineIdentity(_) === lineIdentity(item)),
      remove: async (item, forever) => {
        let payload = [...get().items];
        const itemIndex = get().getItemIndex(item);
        if (item?.quantity <= 1 || forever)
          payload = payload.filter((_, index) => index !== itemIndex);
        else if (itemIndex > -1)
          payload[itemIndex] = {
            ...payload[itemIndex],
            quantity: get().items[itemIndex].quantity - 1,
          };
        await get().setCart(payload);
      },
      add: async (item, extra: any) => {
        const quantity = extra?.quantity ?? (item as CartItem).quantity ?? 1;
        const line = cloneItem({ ...item, quantity } as CartItem);
        let payload = [...get().items];
        const index = get().getItemIndex(line);
        if (index < 0) payload.push(line);
        else {
          payload[index] = {
            ...payload[index],
            quantity: payload[index].quantity + quantity,
          };
        }
        await get().setCart(payload);
      },
      refreshQuote: () =>
        scheduleQuote(() =>
          get().setCart(get().items, { dropUnavailable: true }),
        ),
      setCart: async (items, options) => {
        const local = (items as CartItem[]).filter(
          (line) => !missingRequiredGroup(line),
        );
        if (!local.length) {
          lastQuoteSignature = "";
          inFlight = null;
          inFlightSig = "";
          set({ items: [], totalItemsInCart: 0, ...emptyTotals });
          return;
        }
        const location = place();
        const sig = quoteSignature(location, get().promoCode, local);
        if (sig === lastQuoteSignature) return;
        if (inFlight && inFlightSig === sig) return inFlight;

        const request = (async () => {
          const previousIds = new Set(
            get().items.map((line) => lineIdentity(line)),
          );
          const requestQuote = (lines: CartItem[]) =>
            APIs.cart.calculate({
              areaUuid: location?.area,
              branchUuid: location?.branch,
              promoCode: get().promoCode,
              orderType: location?.orderType,
              cartSnapshot: { items: lines.map(toQuoteItem) },
            });
          const choiceError = (error: any) =>
            /Choose (?:at least (?:one|\d+)|at most \d+|\d+ to \d+|\d+|one) options? for /.test(
              String(error?.message || ""),
            );
          const unavailableError = (error: any) =>
            error?.code === "ITEM_UNAVAILABLE" ||
            error?.code === "UNKNOWN_ITEM";
          const dropLine = (line: CartItem, error: any) => {
            const stale = previousIds.has(lineIdentity(line));
            if (stale && choiceError(error)) return true;
            if (stale && options?.dropUnavailable && unavailableError(error))
              return true;
            return false;
          };
          try {
            let lines = local;
            let payload;
            try {
              set({ calculatingQuote: true });
              payload = await requestQuote(lines);
            } catch (error: any) {
              const recoverable =
                choiceError(error) ||
                (options?.dropUnavailable && unavailableError(error));
              if (lines.length < 2) {
                if (!dropLine(lines[0], error)) throw error;
                lastQuoteSignature = "";
                set({ items: [], totalItemsInCart: 0, ...emptyTotals });
                return;
              }
              if (!recoverable) throw error;
              const kept: CartItem[] = [];
              for (const line of lines) {
                try {
                  await requestQuote([line]);
                  kept.push(line);
                } catch (lineError: any) {
                  if (!dropLine(line, lineError)) throw lineError;
                }
              }
              if (!kept.length) {
                lastQuoteSignature = "";
                set({ items: [], totalItemsInCart: 0, ...emptyTotals });
                return;
              }
              if (kept.length === lines.length) throw error;
              lines = kept;
              payload = await requestQuote(lines);
            } finally {
              set({ calculatingQuote: false });
            }
            if (inFlightSig !== sig) return;
            lastQuoteSignature = quoteSignature(
              location,
              get().promoCode,
              lines,
            );
            const quoted = payload.items ?? [];
            const totals = payload.totals;
            const merged = lines.map((line, index) => {
              const quotedLine = quoted[index] || {};
              return {
                ...line,
                name: quotedLine.name || line.name,
                desc: quotedLine.description || line.desc,
                imageUrl: quotedLine.imageUrl || line.imageUrl,
                choices: quotedLine.choices ?? line.choices,
                unitPrice: quotedLine.unitPrice,
                totalPrice: quotedLine.lineTotal ?? line.totalPrice,
                lineTotal: quotedLine.lineTotal,
                quantity: quotedLine.quantity ?? line.quantity,
              };
            });
            set({
              items: merged,
              tax: totals?.tax ?? 0,
              subtotal: totals?.subtotal ?? 0,
              taxPercent: totals?.taxPercent ?? 0,
              grandtotal: totals?.grandtotal ?? 0,
              deliveryCharges: totals?.deliveryCharges ?? 0,
              discount: totals?.discount ?? 0,
              discountBreakdown: payload.discountBreakdown,
              quoteId: payload.quoteId,
              pricesIncludeTax: totals?.pricesIncludeTax,
              totalItemsInCart: Tools.sum(merged.map((_) => _.quantity)),
            });
          } finally {
            if (inFlightSig === sig) {
              inFlight = null;
              inFlightSig = "";
            }
          }
        })();

        inFlight = request;
        inFlightSig = sig;
        await request;
      },
      reset: () => {
        lastQuoteSignature = "";
        set({
          items: [],
          promoCode: undefined,
          checkoutPrefs: {},
          totalItemsInCart: 0,
          ...emptyTotals,
        });
      },
      checkout: async (deliveryInfo) => {
        const { receipt, idempotencyKey, ...rest } = deliveryInfo ?? {};
        const location = place();
        const order = await APIs.orders.checkout(
          {
            ...rest,
            quoteId: get().quoteId,
            areaUuid: rest.areaUuid || location?.area,
            branchUuid: rest.branchUuid || location?.branch,
            orderType: rest.orderType || location?.orderType,
            promoCode: rest.promoCode || get().promoCode,
            cartSnapshot: { items: get().items.map(toQuoteItem) },
          },
          { receipt, idempotencyKey },
        );
        await get().setCart([]);
        return order;
      },
    }),
    {
      name: "cart-storage-v3",
      version: 1,
      migrate: (persisted: any) => {
        if (persisted?.dishes && !persisted.items) {
          const { dishes, ...rest } = persisted;
          return { ...rest, items: dishes };
        }
        return persisted;
      },
      partialize: (state) => ({
        items: state.items,
        promoCode: state.promoCode,
        checkoutPrefs: state.checkoutPrefs,
      }),
      onRehydrateStorage: () => (state) => {
        const quote = () => state?.refreshQuote().catch(() => {});
        let started = false;
        const run = () => {
          if (started) return;
          started = true;
          quote();
        };
        const unsub = useAppStore.persist.onFinishHydration(() => {
          unsub();
          run();
        });
        if (useAppStore.persist.hasHydrated()) {
          unsub();
          run();
        }
      },
    },
  ),
);

export default useCartStore;
